import { Box } from '@mui/material';
import clsx from 'clsx';
import { useStyles } from './ScheduleAppointment.styles';
import { Icons } from '../../../../../icons';
import { Badge, badgeStyle, badgeType } from '../../../../atoms/badge';
import { btnType, Button } from '../../../../atoms/button';
import { iconBtnPosition, iconBtnType, IconButton } from '../../../../molecules/icon-button';
import { Menu } from '../../../../molecules/menu';
import { colors } from '../../../../../colors';
import { AppointmentStatusText } from '../../../../../constants/CommonConstants';
import dayjs from '../../../../../../utils/dayjs';
import SignOffTodo from '../../../../../icons/lotties/lottieIcons/SignOffTodo';

const ScheduleAppointment = ({
  appointments,
  visitFrequency,
  onSchedule,
  onUpdateVisitFrequency,
  onLoadMoreClick,
  totalAppointments,
  value,
  isLoadingMore,
}) => {
  const classes = useStyles();

  const renderVisitFrequency = () => {
    return (
      <Box className={classes.visitFrequencyContainer}>
        <Box display="flex" alignItems="center" gap="12px">
          <Box>Visit Frequency</Box>
        </Box>
        <Box display="flex" alignItems="center" gap="13px">
          <Box sx={{ fontSize: 16, fontWeight: 500, color: colors.neutral700 }}>{visitFrequency}</Box>
          <Box
            sx={{
              '& button': { gap: 1, padding: 0 },
              '& button svg': {
                width: 16,
                height: 20,
              },
            }}
          >
            <Menu
              icon="more"
              items={[
                {
                  label: 'Update Visit Frequency',
                  onClick: onUpdateVisitFrequency,
                },
              ]}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <Box display="flex" flexDirection="column" gap="16px">
        <Box display="flex" gap="8px" alignItems="center">
        <Box width={80} maxWidth={80} maxHeight={80}>
            <SignOffTodo />
          </Box>
          <Box display="flex" flexDirection="column" gap="4px">
            <span className={classes.emptyTitle}>Always schedule in session</span>
            <span className={classes.emptySubTitle}>
              It's best to discuss the next appointment with the patient
            </span>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderAppointmentCard = () => {
    return (
      <Box display="flex" flexDirection="column" gap="12px" width="100%">
        {appointments.map((item, index) => (
          <Box key={index} className={classes.appointmentCard}>
            <Box
              display="flex"
              flexDirection="column"
              gap="16px"
            >
              <Box gap="4px">
                <Box gap="12px" display="flex" alignItems="center">
                  <Box className={classes.appointmentCardTitle}>{item.serviceName}</Box>
                  <Badge
                    style={badgeStyle.SUCCESS}
                    variant={badgeType.FILLED}
                    size={28}
                  >
                    {AppointmentStatusText[item.status]}
                  </Badge>
                </Box>
                <Box
                  className={classes.appointmentCardSubTitle}
                >{`${item.serviceDuration} minutes session`}</Box>
              </Box>
              <Box className={classes.appointmentCardFooter}>
                <Box className={classes.appointmentCardFooterBlock}>
                  <Icons
                    glyph="calendar-today-outlined"
                    color={colors.neutral600}
                    className={classes.appointmentIcon}
                  />
                  {dayjs(item.startTime).format('MMMM DD, YYYY')}
                </Box>
                <Box className={classes.appointmentCardFooterBlock}>
                  <Icons glyph="clock" color={colors.neutral600} className={classes.appointmentIcon} />
                  {dayjs(item.startTime).format('hh:mmA')} - {dayjs(item.endTime).format('hh:mmA')}
                </Box>
                {item.recurringType && (
                  <Box className={classes.appointmentCardFooterBlock}>
                    <Icons glyph="rotate" color={colors.neutral600} className={classes.appointmentIcon} />
                    Recurring
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ))}
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.loadingWrap}>
          {isLoadingMore ? (
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
          ) : (
            appointments.length < totalAppointments && (
              <Button variant={btnType.TEXT} className={classes.loadMoreBtn} onClick={onLoadMoreClick}>
                Load more
              </Button>
            )
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.appointmentListContainer}>
      {renderVisitFrequency()}
      <Box className={classes.appointmentCardContainer}>
        <Box className={classes.appointmentHeader}>
          <Box>Appointments</Box>
          <Box>
            <IconButton
              className={classes.addAppointmentBtn}
              icon="plus"
              variant={iconBtnType.TEXT}
              onClick={onSchedule}
              iconPosition={iconBtnPosition.LEFT}
            >
              Schedule New
            </IconButton>
          </Box>
        </Box>
        <Box
          className={clsx(classes.appointmentBody, {
            [classes.appointmentDetailsBody]: appointments.length !== 0,
          })}
        >
          {appointments.length === 0 && !isLoadingMore ? renderEmptyState() : renderAppointmentCard()}
        </Box>
      </Box>
    </Box>
  );
};

export { ScheduleAppointment };
